import type { Account } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';
import type { CardWithHelpers } from '@shared/cards-types';
import { getCardAccount } from '@shared/cards-utils';

import { getAccount, getCardFromProduct, getTransactionAccount } from './RetailProducts';

/**
 * Returns the Account in which the Transaction (represented by ownerId, cardId)
 * is booked.
 *
 * For Credit Card Transactions, the Main Credit Card "Account" associated with
 * the owner of the Transaction will be returned instead of a real Account.
 *
 * @see getCardAccount()
 * @see getHistoryAccount()
 *
 * Account - Card relationship:
 *
 * useProducts() (GAPI & WebAPI):
 * {
 *   id: ACCOUNT_ID-CARD_ID,
 *   card: {
 *     id: CARD_ID,
 *     _shadowAccountId: ACCOUNT_ID (Credit Cards only)
 *   },
 *   transactionAccountId: ACCOUNT_ID
 * }
 *
 * useCards() (WebAPI):
 * {
 *   id: CARD_ID,
 *   cz-uniqueId?: UNIQUE_CARD_ID (CZ only, @see CardFieldsCZ.cz-uniqueId)
 *   mainAccount: {
 *     id: ACCOUNT_ID
 *   }
 * }
 */
export function getOwnerAccount(
    products: Array<ProductWithHelpers<Account>>,
    cards: Array<CardWithHelpers>,
    ownerId: string,
    cardId?: string,
): ProductWithHelpers<Account> | undefined {
    return (
        getCardAccount(products, cards, ownerId, cardId) ||
        getAccount(products, ownerId) ||
        getTransactionAccount(products, ownerId)
    );
}

/**
 * Returns the Account in whose History the Transaction (represented by ownerId,
 * cardId) would be shown.
 *
 * This is used to link a Transaction to an Account History page.
 *
 * @see getOwnerAccount()
 */
export function getHistoryAccount(
    products: Array<ProductWithHelpers<Account>>,
    cards: Array<CardWithHelpers>,
    ownerId: string,
    cardId?: string,
): ProductWithHelpers<Account> | undefined {
    const ownerAccount = getOwnerAccount(products, cards, ownerId, cardId);
    const card = ownerAccount && getCardFromProduct(ownerAccount);
    const backingAccountId = card?.mainAccount?.id;
    const backingAccount = backingAccountId ? getAccount(products, backingAccountId) : undefined;
    return backingAccount ?? ownerAccount;
}
